import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _, { Dictionary } from 'lodash';
import { forkJoin } from 'rxjs';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import {
   BrickGridColumnConfigRemoteDto,
   BrickGridSettingsRemoteDto,
   BrickWallService,
   UpdateBrickGridSettingsRequestData,
} from '../../../../../../generated/swagger';
import { ColumnSetting, GridSettings } from '../../../../../samwebDescription/samweb/dashboard/grid-column-settings';
import {
   BrickGridColumnChanged,
   BrickGridColumnOrderChanged,
   BrickGridSettingsUpdatedByServer,
   PersistAllGridColumnSettings,
   PersistGridColumnSettings,
} from './brick-grids.actions';

export class SettingItem {
   constructor(field: string, index: number) {
      this.field = field;
      this.index = index;
   }
   field: string;
   index: number;
}

export interface BrickGridStateStateModel {
   brickGridStates: { [brickInstanceId: string]: { [gridId: string]: GridSettings } };
   unsavedGridsOnBrickInstances: string[];
}

@State<BrickGridStateStateModel>({
   name: 'brickGrids',
   defaults: {
      brickGridStates: {},
      unsavedGridsOnBrickInstances: [],
   },
})
@Injectable()
export class BrickGridsState {
   constructor(private brickWallService: BrickWallService, private store: Store) {}
   @Selector([BrickGridsState])
   public static getBrickColumnConfig(state: BrickGridStateStateModel): (brickInstanceId: string, gridId: string) => GridSettings {
      return (brickInstanceId: string, gridId: string) => {
         const gridStatesForBrick = _.cloneDeep(state.brickGridStates[brickInstanceId]);
         if (_.isNil(gridStatesForBrick) || _.isNil(gridStatesForBrick[gridId])) {
            return null;
         }
         _(gridStatesForBrick[gridId].columnsConfig).forEach((element) => {
            gridStatesForBrick[gridId].columnsConfig[element.field] = <ColumnSetting>{
               field: element.field,
               isHidden: element.isHidden,
               orderIndex: element.orderIndex,
               width: element.width ?? 200,
            };
         });
         return gridStatesForBrick[gridId];
      };
   }

   @Selector([BrickGridsState.getBrickColumnConfig])
   public static getBrickColumnConfiguration(
      getBrickColumnConfig: (brickInstanceId: string, gridId: string) => GridSettings
   ): (brickInstanceId: string, gridId: string) => GridSettings {
      return (brickInstanceId: string, gridId: string) => {
         return getBrickColumnConfig(brickInstanceId, gridId);
      };
   }

   // NOTE: The available directory columns selector is necessary to ensure the settings are applied when all columns are loaded.
   // Please only remove it if you have a fitting replacement.
   @Selector([BrickGridsState.getBrickColumnConfig, DirectoryStructureState.availableDirectoryColumns])
   public static getBrickColumnConfigurationForDirectoryBrick(
      getBrickColumnConfig: (brickInstanceId: string, gridId: string) => GridSettings,
      directoryColumns: DirectoryColumn[]
   ): (brickInstanceId: string, gridId: string) => GridSettings {
      return (brickInstanceId: string, gridId: string) => {
         return getBrickColumnConfig(brickInstanceId, gridId);
      };
   }

   @Action(BrickGridSettingsUpdatedByServer)
   @ImmutableContext()
   public brickGridSettingsUpdatedByServer(ctx: StateContext<BrickGridStateStateModel>, action: BrickGridSettingsUpdatedByServer) {
      const state = ctx.getState();

      _.forEach(action.data.brickGridSettings, (value, brickInstanceId) => {
         let brickGridSettings = state.brickGridStates[brickInstanceId];
         if (_.isNil(brickGridSettings)) {
            brickGridSettings = {};
            state.brickGridStates[brickInstanceId] = brickGridSettings;
         }

         _.forEach(value.brickGridSettings, (gridConfig, gridId) => {
            brickGridSettings[gridId] = <GridSettings>{
               columnsConfig: _.mapValues(
                  gridConfig.columnConfigs,
                  (x) =>
                     <ColumnSetting>{
                        field: x.field,
                        isHidden: x.isHidden,
                        orderIndex: x.orderIndex,
                        width: x.width,
                     }
               ),
            };
         });
      });
      ctx.setState(state);
   }

   @Action(BrickGridColumnChanged)
   @ImmutableContext()
   public brickGridColumnChanged(ctx: StateContext<BrickGridStateStateModel>, action: BrickGridColumnChanged) {
      ctx.setState((state) => {
         const gridStatesForBrick = state.brickGridStates[action.brickInstanceId] ? state.brickGridStates[action.brickInstanceId] : {};
         const gridConfigForBrickId: GridSettings = gridStatesForBrick[action.gridId] ? gridStatesForBrick[action.gridId] : <GridSettings>{ columnsConfig: {} };
         action.columnSettings.forEach((changedColumn) => {
            const prevSettings = gridConfigForBrickId.columnsConfig[changedColumn.field];
            const newSettings = { ...prevSettings, ...changedColumn };
            gridConfigForBrickId.columnsConfig[changedColumn.field] = newSettings;
         });

         gridStatesForBrick[action.gridId] = gridConfigForBrickId;
         state.brickGridStates[action.brickInstanceId] = gridStatesForBrick;

         state.unsavedGridsOnBrickInstances.push(action.brickInstanceId);

         return state;
      });

      return ctx.dispatch(new PersistGridColumnSettings(action.brickInstanceId));
   }

   @Action(BrickGridColumnOrderChanged)
   @ImmutableContext()
   public brickGridColumnOrderChanged(ctx: StateContext<BrickGridStateStateModel>, action: BrickGridColumnOrderChanged) {
      ctx.setState((state) => {
         const gridStatesForBrick = state.brickGridStates[action.brickInstanceId] ? state.brickGridStates[action.brickInstanceId] : {};
         const gridConfigForBrickId: GridSettings = gridStatesForBrick[action.gridId] ? gridStatesForBrick[action.gridId] : <GridSettings>{ columnsConfig: {} };

         const newIndex = action.newOrderIndex;
         const oldIndex = action.oldOrderIndex;

         const columnSettings = _(
            _.values(gridConfigForBrickId.columnsConfig)
               .filter((setting: ColumnSetting) => !_.isNil(setting.orderIndex))
               .map((setting: ColumnSetting) => new SettingItem(setting.field, setting.orderIndex))
         ).value();

         if (oldIndex < newIndex) {
            _(columnSettings)
               .filter((setting) => setting.index > oldIndex && setting.index <= newIndex)
               .forEach((setting) => {
                  gridConfigForBrickId.columnsConfig[setting.field].orderIndex = gridConfigForBrickId.columnsConfig[setting.field].orderIndex - 1;
               });
         }

         if (oldIndex > newIndex) {
            _(columnSettings)
               .filter((setting) => setting.index < oldIndex && setting.index >= newIndex)
               .forEach((setting) => {
                  gridConfigForBrickId.columnsConfig[setting.field].orderIndex = gridConfigForBrickId.columnsConfig[setting.field].orderIndex + 1;
               });
         }

         action.targetColumnSetting.orderIndex = newIndex;
         gridConfigForBrickId.columnsConfig[action.targetColumnSetting.field] = action.targetColumnSetting;

         gridStatesForBrick[action.gridId] = gridConfigForBrickId;
         state.brickGridStates[action.brickInstanceId] = gridStatesForBrick;

         state.unsavedGridsOnBrickInstances.push(action.brickInstanceId);

         return state;
      });
      return ctx.dispatch(new PersistGridColumnSettings(action.brickInstanceId));
   }

   @Action(PersistGridColumnSettings)
   public persistUnsavedGridColumnSettings(ctx: StateContext<BrickGridStateStateModel>, action: PersistGridColumnSettings) {
      const state = ctx.getState();

      const grids = state.brickGridStates[action.brickInstanceId];
      const data: Dictionary<BrickGridSettingsRemoteDto> = _.mapValues(grids, (grid) => {
         return <BrickGridSettingsRemoteDto>{
            columnConfigs: _.mapValues(grid.columnsConfig, (columnConfig) => {
               return <BrickGridColumnConfigRemoteDto>{
                  field: columnConfig.field,
                  width: columnConfig.width,
                  isHidden: columnConfig.isHidden,
                  orderIndex: columnConfig.orderIndex,
               };
            }),
         };
      });

      ctx.patchState({
         unsavedGridsOnBrickInstances: [],
      });

      return this.brickWallService.brickGridSettings(<UpdateBrickGridSettingsRequestData>{
         brickInstanceId: action.brickInstanceId,
         brickGridSettings: data,
      });
   }

   @Action(PersistAllGridColumnSettings)
   public persistAllGridColumnSettings(ctx: StateContext<BrickGridStateStateModel>, action: PersistAllGridColumnSettings) {
      const observables = action.brickIds.map((brickInstanceId) => ctx.dispatch(new PersistGridColumnSettings(brickInstanceId)));
      if (_.isEmpty(observables)) {
         return;
      }

      return forkJoin(observables);
   }
}
